import type React from 'react';
import { useRef, useState } from 'react';

export type FieldValidator = (value: string) => string | undefined;

export type FormErrors<Values> = Partial<Record<keyof Values, string>>;

export interface ValidatedForm<Values extends { [Field in keyof Values]: string }> {
  values: Values;
  errors: FormErrors<Values>;
  change: (field: keyof Values) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  blur: (field: keyof Values) => (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Checks every field, shows the errors and returns the first invalid field in `validators` order. */
  validateAll: () => keyof Values | undefined;
}

/**
 * Field values and errors for a form of text inputs. A field is checked when it loses focus once it has been
 * edited, re-checked as it changes while it shows an error, and every field is checked by `validateAll`.
 */
export const useValidatedForm = <Values extends { [Field in keyof Values]: string }>(
  initial: Values,
  validators: Record<keyof Values, FieldValidator>,
): ValidatedForm<Values> => {
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<FormErrors<Values>>({});
  const edited = useRef(new Set<keyof Values>());

  const check = (field: keyof Values, value: string) =>
    setErrors((prev) => ({ ...prev, [field]: validators[field](value) }));

  const change = (field: keyof Values) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    edited.current.add(field);
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) check(field, value);
  };

  const blur = (field: keyof Values) => (event: React.FocusEvent<HTMLInputElement>) => {
    if (edited.current.has(field)) check(field, event.target.value);
  };

  const validateAll = () => {
    const next: FormErrors<Values> = {};
    let first: keyof Values | undefined;
    for (const field of Object.keys(validators) as (keyof Values)[]) {
      const error = validators[field](values[field]);
      if (!error) continue;
      next[field] = error;
      first ??= field;
    }
    setErrors(next);
    return first;
  };

  return { values, errors, change, blur, validateAll };
};
